/**
 * Library cache yardımcıları
 * ["library", ...] altındaki tüm query'lerde (sayfalı, infinite, tekil) item'ları yerinde günceller.
 */

import type { QueryClient } from "@tanstack/react-query";
import type { InfiniteData } from "@tanstack/react-query";
import type { LibraryItem, LibraryResponse } from "../models/api-types";

type LibraryCacheEntry =
  | InfiniteData<LibraryResponse>
  | LibraryResponse
  | LibraryItem
  | undefined;

const patchWhere = (
  queryClient: QueryClient,
  matches: (item: LibraryItem) => boolean,
  patch: (item: LibraryItem) => LibraryItem
) => {
  const patchItem = (item: LibraryItem) => (matches(item) ? patch(item) : item);
  queryClient.setQueriesData<LibraryCacheEntry>(
    { queryKey: ["library"] },
    (old) => {
      if (!old) return old;
      if ("pages" in old) {
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            result: page.result.map(patchItem),
          })),
        };
      }
      if ("result" in old && Array.isArray(old.result)) {
        return {
          ...old,
          result: old.result.map(patchItem),
        };
      }
      if ("_id" in old) {
        return patchItem(old);
      }
      return old;
    }
  );
};

export const patchLibraryItemsInCache = (
  queryClient: QueryClient,
  ids: string[],
  patch: (item: LibraryItem) => LibraryItem
) => {
  const idSet = new Set(ids);
  patchWhere(queryClient, (item) => idSet.has(item._id), patch);
};

/** Aynı mesaja ait tüm öğeleri günceller (ör. mesaj bazlı görünürlük) */
export const patchLibraryMessagesInCache = (
  queryClient: QueryClient,
  messageIds: string[],
  patch: (item: LibraryItem) => LibraryItem
) => {
  const idSet = new Set(messageIds);
  patchWhere(
    queryClient,
    (item) => !!item.messageId?._id && idSet.has(item.messageId._id),
    patch
  );
};
